/**
 * MultiChoice Field Default Configuration
 *
 * The most complex field type with extensive styling, layout,
 * and behavioral options for multiple selection scenarios.
 */

// ============================================================================
// Type Definitions
// ============================================================================

export type MultiChoiceStyle = 'Chip' | 'Checkbox' | 'Radio Button' | 'Dropdown';
export type ChipSize = 'Compact' | 'Medium' | 'Large';
export type ImageDisplay = 'Fit' | 'Fill' | 'Stretch';
export type LayoutStyle = 'Standard' | 'Padded Frame';
export type TextAlignment = 'Left' | 'Centre' | 'Right';
export type ColumnSelection = 'Auto' | '1' | '2' | '3' | '4';
export type LayoutDirection = 'Horizontal' | 'Vertical';
export type ChipWidth = 'Fixed' | 'Dynamic';
export type ChipLabelPosition = 'Right side of image' | 'Below Image' | 'Show only on hover' | 'Hide label';
export type LoadOption = 'All' | 'Partial';
export type LoadRemainingOption = 'Show All' | 'In Batch';
export type ExcludedOption = 'Hide' | 'Disable' | 'Disable and moved to the bottom of the list';
export type InfoDisplay = 'Inside' | 'Outside';
export type InfoTrigger = 'Hover' | 'Click';
export type InfoSize = 'Auto' | 'Small' | 'Medium' | 'Large';
export type InfoForOptionsType = 'Icon' | 'Inline';

// Choice type for multichoice options
export interface Choice {
  id: string;
  label: string;
  value: string;
  imageUrl?: string;
  disabled?: boolean;
}

export interface MultiChoiceFieldConfig {
  // Basic Settings
  label: string;
  showTitle: boolean;
  exclusionType: 'hide' | 'disable';
  displayName: string;
  shortDisplayName: string;

  // Style Settings
  style: MultiChoiceStyle;
  layoutDirection: LayoutDirection;
  chipSize: ChipSize;
  showImage: boolean;
  imageDisplay: ImageDisplay;
  layoutStyle: LayoutStyle;
  captionAlignment: TextAlignment;
  columnSelection: ColumnSelection;
  fillDirection: LayoutDirection;
  chipWidth: ChipWidth;
  chipLabelPosition: ChipLabelPosition;
  showSearchBar: boolean;
  showExpandCollapse: boolean;
  collapseByDefault: boolean;

  // Load Options Settings
  loadOption: LoadOption;
  loadMoreButtonLabel: string;
  loadLessButtonLabel: string;
  initialLoadCount: number;
  loadRemainingOption: LoadRemainingOption;
  batchCount: number;

  // Excluded Options Settings
  excludedOption: ExcludedOption;
  hideWhenNoOptions: boolean;

  // Show Info for Field
  showInfoForField: boolean;
  infoForFieldDisplay: InfoDisplay;
  infoForFieldTrigger: InfoTrigger;
  infoForFieldSize: InfoSize;
  infoForFieldOpenInWindow: boolean;
  // Show Info for Options
  showInfoForOptions: boolean;
  infoForOptionsType: InfoForOptionsType;
  infoForOptionsDisplay: InfoDisplay;
  infoForOptionsTrigger: InfoTrigger;
  infoForOptionsSize: InfoSize;
  infoForOptionsOpenInWindow: boolean;

  // Advanced Settings
  vda: boolean;  // Variant Defining Attribute

  // API alignment properties
  required: boolean;
  choices: Choice[];
}

// ============================================================================
// Default Configuration
// ============================================================================

export const MULTICHOICE_DEFAULT_CONFIG: Omit<MultiChoiceFieldConfig, 'label' | 'displayName'> = {
  // Basic
  showTitle: true,
  exclusionType: 'hide',
  shortDisplayName: '',

  // Style Configuration
  style: 'Chip',
  layoutDirection: 'Horizontal',
  chipSize: 'Medium',
  showImage: false,
  imageDisplay: 'Fit',
  layoutStyle: 'Standard',
  captionAlignment: 'Centre',
  columnSelection: 'Auto',
  fillDirection: 'Vertical',
  chipWidth: 'Dynamic',
  chipLabelPosition: 'Below Image',
  showSearchBar: false,
  showExpandCollapse: false,
  collapseByDefault: false,
  // Load Options
  loadOption: 'All',
  loadMoreButtonLabel: 'Show More',
  loadLessButtonLabel: 'Show Less',
  initialLoadCount: 5,
  loadRemainingOption: 'Show All',
  batchCount: 5,
  // Excluded Options
  excludedOption: 'Disable',
  hideWhenNoOptions: false,
  // Show Info for Field
  showInfoForField: true,
  infoForFieldDisplay: 'Inside',
  infoForFieldTrigger: 'Hover',
  infoForFieldSize: 'Small',
  infoForFieldOpenInWindow: false,
  // Show Info for Options
  showInfoForOptions: false,
  infoForOptionsType: 'Icon',
  infoForOptionsDisplay: 'Inside',
  infoForOptionsTrigger: 'Hover',
  infoForOptionsSize: 'Small',
  infoForOptionsOpenInWindow: false,
  // Advanced
  vda: false,
  // API alignment
  required: false,
  choices: [],
};

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create default configuration for a multichoice field
 * @param fieldName The field name to use as label
 */
export const createMultiChoiceConfig = (fieldName: string): MultiChoiceFieldConfig => ({
  ...MULTICHOICE_DEFAULT_CONFIG,
  label: fieldName,
  displayName: '',
});

// ============================================================================
// Style-Specific Default Overrides
// ============================================================================

/**
 * Get style-specific default overrides
 * Different styles have different sensible defaults
 */
export const getStyleDefaults = (style: MultiChoiceStyle): Partial<MultiChoiceFieldConfig> => {
  switch (style) {
    case 'Chip':
      return {
        layoutDirection: 'Horizontal',
        chipSize: 'Medium',
        chipWidth: 'Dynamic',
      };
    case 'Checkbox':
      return {
        layoutDirection: 'Vertical',
        columnSelection: '1',
      };
    case 'Radio Button':
      return {
        layoutDirection: 'Vertical',
        columnSelection: '1',
      };
    case 'Dropdown':
      return {
        showSearchBar: true,
      };
    default:
      return {};
  }
};

/**
 * Create configuration for a specific style
 */
export const createMultiChoiceConfigForStyle = (
  fieldName: string,
  style: MultiChoiceStyle
): MultiChoiceFieldConfig => ({
  ...createMultiChoiceConfig(fieldName),
  style,
  ...getStyleDefaults(style),
});

export default createMultiChoiceConfig;
